import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import { instanceI18n } from '@/plugins/i18n'

type ConnectivitySource = 'network' | 'websocket' | 'sip'

const SERVICE_LABEL_KEYS: Record<ConnectivitySource, string> = {
  network: 'common.connectivity.services.network',
  websocket: 'common.connectivity.services.websocket',
  sip: 'common.connectivity.services.sip',
}

export const useConnectivityStore = defineStore('connectivity', () => {
  const t = instanceI18n.global.t
  const networkOnline = ref(typeof navigator === 'undefined' ? true : navigator.onLine)
  const webSocketConnected = ref(true)
  const sipConnected = ref(true)
  const listenersInstalled = ref(false)

  const isAnyServiceDisconnected = computed(
    () => !networkOnline.value || !webSocketConnected.value || !sipConnected.value,
  )

  const affectedServices = computed(() => {
    const services: string[] = []
    if (!networkOnline.value) services.push(t(SERVICE_LABEL_KEYS.network))
    if (!webSocketConnected.value) services.push(t(SERVICE_LABEL_KEYS.websocket))
    if (!sipConnected.value) services.push(t(SERVICE_LABEL_KEYS.sip))
    return services
  })

  const primaryFailure = computed<ConnectivitySource | null>(() => {
    if (!networkOnline.value) return 'network'
    if (!webSocketConnected.value) return 'websocket'
    if (!sipConnected.value) return 'sip'
    return null
  })

  const primaryMessage = computed(() => {
    if (!primaryFailure.value) return ''

    if (affectedServices.value.length > 1) {
      return t('common.connectivity.messages.multiple', { services: affectedServices.value.join(', ') })
    }

    return t(`common.connectivity.messages.${primaryFailure.value}`)
  })

  const setNetworkOnline = (isOnline: boolean) => {
    networkOnline.value = isOnline
  }

  const setWebSocketConnected = (isConnected: boolean) => {
    webSocketConnected.value = isConnected
  }

  const setSipConnected = (isConnected: boolean) => {
    sipConnected.value = isConnected
  }

  const handleOnline = () => setNetworkOnline(true)
  const handleOffline = () => setNetworkOnline(false)

  const installNetworkListeners = () => {
    if (listenersInstalled.value || typeof window === 'undefined') return

    // El store centraliza la fuente de verdad para no duplicar listeners globales.
    setNetworkOnline(navigator.onLine)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    listenersInstalled.value = true
  }

  const removeNetworkListeners = () => {
    if (!listenersInstalled.value || typeof window === 'undefined') return

    window.removeEventListener('online', handleOnline)
    window.removeEventListener('offline', handleOffline)
    listenersInstalled.value = false
  }

  return {
    networkOnline,
    webSocketConnected,
    sipConnected,
    isAnyServiceDisconnected,
    affectedServices,
    primaryFailure,
    primaryMessage,
    setNetworkOnline,
    setWebSocketConnected,
    setSipConnected,
    installNetworkListeners,
    removeNetworkListeners,
  }
})
